import type { ISiteLocations } from '../interfaces/IScheduler.js';
import { UnauthorizedError } from './errors.js';
import type { AuthedFetch } from './transport.js';

async function handle(res: Response): Promise<never> {
  const body = await res.json().catch(() => null);
  // 401 reaches us only after authedFetch's refresh-and-retry has already
  // failed and dispatched `dorca:auth-expired`; we just surface the error
  // type so React Query lands in an error state until the redirect fires.
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`request failed: ${res.status} ${body?.error?.message ?? ''}`);
}

/**
 * The sites API over an injected transport — each app binds its own
 * `authedFetch` in its `apis/sites.ts`.
 */
export function createSitesApi(authedFetch: AuthedFetch) {
  /**
   * Building area catalog (floors → zones) for a site, backing the Scheduler's
   * Locations tab. Sourced from the site's linked Trusstor project on the backend.
   * Throws on non-2xx; the useSiteLocations hook falls back to an empty catalog so
   * an unlinked site degrades to the tab's "no areas" placeholder rather than erroring.
   */
  async function fetchSiteLocations(siteId: string, signal?: AbortSignal): Promise<ISiteLocations> {
    const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/locations`, { signal });
    if (!res.ok) return handle(res);
    return (await res.json()) as ISiteLocations;
  }

  return { fetchSiteLocations };
}
